/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface DepartmentProps {
  id: string;
  name: string;
  code: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class DepartmentRecord extends Record<DepartmentProps>({
  id: null as any,
  name: null as any,
  code: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    name: string;
    code: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): DepartmentRecord {
    if (json == null) {
      throw new Error('DepartmentRecord.fromJS: parameter cannot be null');
    }
    return new DepartmentRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'DepartmentJson.id',
      ),
      name: getFromOrThrow(json, (j) => j.name, 'DepartmentJson.name'),
      code: getFromOrThrow(json, (j) => j.code, 'DepartmentJson.code'),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    name: string;
    code: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [DepartmentRecord, Error[]] {
    if (json == null) {
      throw new Error('DepartmentRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new DepartmentRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'DepartmentJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      name: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.name, 'DepartmentJson.name'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      code: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.code, 'DepartmentJson.code'),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: DepartmentProps) {
    super(props);
    if (props == null) {
      throw new Error('DepartmentRecord constructor parameter cannot be null');
    }
  }
}

export abstract class DepartmentViewModelBase implements DepartmentProps {
  protected readonly _props: DepartmentProps;
  private _json?: DepartmentProps;

  get id(): string {
    return this._props.id;
  }
  get name(): string {
    return this._props.name;
  }
  get code(): string {
    return this._props.code;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: DepartmentProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        name: this._props.name,
        code: this._props.code,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllDepartmentEntities = (state: AppState) =>
  state.entityStores.department.entities;

export const selectDepartmentById = (id: string) => (state: AppState) =>
  state.entityStores.department.entities.get(id);

export const selectDepartmentMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.department.meta.get(id);

export const selectDepartmentAndMetadataById = (id: string) =>
  createSelector(
    selectDepartmentById(id),
    selectDepartmentMetadataById(id),
    (department, meta) => ({ department, meta }),
  );

export function selectDepartmentEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllDepartmentEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, DepartmentRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectDepartmentEntitiesByName(name: string) {
  return createSelector(selectAllDepartmentEntities, (entities) =>
    entities.filter((e) => e.name === name).valueSeq(),
  );
}

export function selectDepartmentEntitiesByCode(code: string) {
  return createSelector(selectAllDepartmentEntities, (entities) =>
    entities.filter((e) => e.code === code).valueSeq(),
  );
}
//#endregion Selectors
